// shell/utils/format.ts — Shared formatting utilities
// ============================================================================

// -- Date helpers ------------------------------------------------------------

function toDate(value: Date | string): Date {
    return typeof value === 'string' ? new Date(value) : value;
}

/** Format date only (e.g., "Jan 5, 2026"). */
export function formatDate(dateOrStr: Date | string): string {
    return toDate(dateOrStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/** Format date and time (e.g., "Jan 5, 2026, 2:30 PM"). */
export function formatDateTime(dateOrStr: Date | string): string {
    return toDate(dateOrStr).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

/** Format as relative time (e.g., "2 hours ago", "in 3 days"). */
export function formatRelative(dateOrStr: Date | string): string {
    const date = toDate(dateOrStr);
    const now = Date.now();
    const diff = date.getTime() - now;
    const absDiff = Math.abs(diff);

    const seconds = Math.floor(absDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    const isPast = diff < 0;

    if (seconds < 60) {
        return isPast ? 'just now' : 'in a moment';
    }

    let value: number;
    let unit: string;

    if (years > 0) {
        value = years;
        unit = years === 1 ? 'year' : 'years';
    } else if (months > 0) {
        value = months;
        unit = months === 1 ? 'month' : 'months';
    } else if (weeks > 0) {
        value = weeks;
        unit = weeks === 1 ? 'week' : 'weeks';
    } else if (days > 0) {
        value = days;
        unit = days === 1 ? 'day' : 'days';
    } else if (hours > 0) {
        value = hours;
        unit = hours === 1 ? 'hour' : 'hours';
    } else {
        value = minutes;
        unit = minutes === 1 ? 'minute' : 'minutes';
    }

    return isPast ? `${value} ${unit} ago` : `in ${value} ${unit}`;
}

/** Format ISO date string as compact relative time: "just now", "5m ago", "2h ago", "3d ago", or "Feb 16". */
export function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Format ISO date string as short time: "14:30". */
export function formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format an ISO timestamp as time-only when it falls on the local current
 * date, or as a full date+time otherwise. 24-hour, zero-padded so values
 * stay aligned in tabular layouts and unambiguous across locales. Pass
 * <code>{ seconds: true }</code> for HH:MM:SS precision.
 */
export function formatDateTimeSmart(dateStr: string, opts?: { seconds?: boolean }): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const withSeconds = opts?.seconds === true;
    const timePart = withSeconds
        ? `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        : `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    const sameDay = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();
    if (sameDay) return timePart;
    const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return `${ymd} ${timePart}`;
}

// -- Duration helpers --------------------------------------------------------

/** Format seconds as HH:MM:SS for timer displays. */
export function formatTimerDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
    ].join(':');
}

/** Format seconds as compact duration: "2h 30m", "5m", "30s". */
export function formatDuration(seconds: number): string {
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) {
        return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/** Format seconds as compact duration: "2h 30m", "5m", "<1m", "0m". */
export function formatDurationShort(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    if (totalSeconds > 0) {
        return '<1m';
    }
    return '0m';
}

// -- Pluralization helpers ---------------------------------------------------

/** ISO 639-1 language code accepted by the pluralization helpers. */
export type PluralizeLang = 'en';

function assertSupportedLang(lang: PluralizeLang): void {
    if (lang !== 'en') {
        throw new Error(`pluralize: unsupported language '${lang}' (only 'en' is supported)`);
    }
}

/** Pluralize an English singular noun using simple suffix rules. */
export function pluralize(singular: string, lang: PluralizeLang = 'en'): string {
    assertSupportedLang(lang);
    if (/(s|x|z|ch|sh)$/i.test(singular)) return `${singular}es`;
    if (/[^aeiou]y$/i.test(singular)) return `${singular.slice(0, -1)}ies`;
    return `${singular}s`;
}

/**
 * Format a count of items with their type label, e.g.
 *   pluralizeCount('version', 0) → 'no versions'
 *   pluralizeCount('version', 1) → '1 version'
 *   pluralizeCount('version', 5) → '5 versions'
 */
export function pluralizeCount(singular: string, count: number, lang: PluralizeLang = 'en'): string {
    assertSupportedLang(lang);
    if (count === 0) return `no ${pluralize(singular, lang)}`;
    if (count === 1) return `1 ${singular}`;
    return `${count} ${pluralize(singular, lang)}`;
}

// -- Number/currency helpers -------------------------------------------------

/** Format bytes as human-readable file size (e.g., "1.5 MB"). */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);

    return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/** Format cents as currency string using Intl.NumberFormat. */
export function formatCurrency(cents: number, currency: string): string {
    const value = cents / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
    }).format(value);
}
